import type { OrderDoc } from '../redux/OrdersSlice';
import store from '../redux/Store';
import { addNotification } from '../redux/notificationsSlice';
import { AppSnackbar } from './AppSnackbar';
import { showLocalPushNotification } from './localPushNotification';
import {
  canEmployeeCheckout,
  isEmployeeWaiterRole,
  normalizeEmployeeRole,
} from '../utils/roles';

export type WaiterOrderEventType = 'created' | 'updated';

const DEDUPE_TTL_MS = 8000;
const recentKeys = new Map<string, number>();

function pruneDedupe(now: number) {
  for (const [key, ts] of recentKeys) {
    if (now - ts > DEDUPE_TTL_MS) recentKeys.delete(key);
  }
}

function shouldDedupe(key: string): boolean {
  const now = Date.now();
  pruneDedupe(now);
  if (recentKeys.has(key)) return true;
  recentKeys.set(key, now);
  return false;
}

function resolveWaiterName(order: OrderDoc): string {
  const name = String(order.bp_order_master_employee_information || '').trim();
  if (name) return name;
  const assigned = String(
    order.bp_order_assigned_staff_information?.bp_employee_name || '',
  ).trim();
  return assigned || 'A waiter';
}

function buildCopy(eventType: WaiterOrderEventType, order: OrderDoc) {
  const waiterName = resolveWaiterName(order);
  const customer = String(order.bp_order_customer_information || '').trim();
  const receipt = String(
    order.bp_order_master_receipt_reference_id || order.bp_order_id || '',
  ).trim();

  const title =
    eventType === 'created'
      ? 'Waiter created an order'
      : 'Waiter updated an order';

  const parts = [
    `${waiterName} ${eventType === 'created' ? 'created' : 'updated'} an order`,
  ];
  if (customer) parts.push(customer);
  if (receipt) parts.push(`#${receipt}`);

  return {
    title,
    body: parts.join(' · '),
    waiterName,
  };
}

/**
 * Notify the logged-in cashier (non-waiter) that a waiter created/updated an order.
 * OS local notification + in-app snackbar + notifications inbox.
 */
export async function notifyCashierOfWaiterOrder(args: {
  eventType: WaiterOrderEventType;
  order: OrderDoc;
}): Promise<void> {
  const { eventType, order } = args;
  if (!order?.bp_order_id) return;

  if (!isEmployeeWaiterRole(order.bp_order_master_employee_role)) {
    return;
  }

  const employeeInfo = store.getState().auth.inApp.account.employeeInfo as
    | Record<string, any>
    | undefined;
  const loggedInRole = employeeInfo?.bp_employee_role;
  if (!canEmployeeCheckout(loggedInRole)) {
    return;
  }

  const creatorId = String(order.bp_order_master_employee_id || '').trim();
  const selfId = String(
    employeeInfo?._id || employeeInfo?.bp_employee_id || '',
  ).trim();
  if (creatorId && selfId && creatorId === selfId) {
    return;
  }

  const dedupeKey = `${eventType}:${order.bp_order_id}:${order.bp_order_master_updated_at || ''}`;
  if (shouldDedupe(dedupeKey)) return;

  const { title, body } = buildCopy(eventType, order);
  const data = {
    type: 'waiter_order',
    eventType,
    orderId: String(order.bp_order_id),
    role: normalizeEmployeeRole(order.bp_order_master_employee_role),
  };

  store.dispatch(
    addNotification({
      title,
      body,
      source: 'local',
      data,
    }),
  );

  AppSnackbar.show(body, 'info', { durationMs: 5000 });

  await showLocalPushNotification({
    id: `waiter-order-${order.bp_order_id}-${eventType}`,
    title,
    body,
    data,
  });
}

/**
 * Resolve created/updated from stream payload.
 * Only accepts explicit `eventType` (or `event` set to created/updated).
 * Does NOT infer from Redux — reconnect/reconcile clears caches and would
 * misclassify existing orders as "created".
 */
export function resolveWaiterOrderEventType(data: {
  eventType?: string;
  event?: string;
  order?: OrderDoc;
}): WaiterOrderEventType | null {
  const fromEventType = String(data.eventType || '')
    .trim()
    .toLowerCase();
  if (fromEventType === 'created' || fromEventType === 'updated') {
    return fromEventType;
  }

  const fromEvent = String(data.event || '')
    .trim()
    .toLowerCase();
  if (fromEvent === 'created' || fromEvent === 'updated') {
    return fromEvent;
  }

  return null;
}
